class MCartAddPopup extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      overlay: '.m-cart-add-popup__overlay',
      closeButton: '.m-cart-add-popup__close',
      addedItemImage: '[data-added-item-image]',
      addedItemTitle: '[data-added-item-title]',
      addedItemPrice: '[data-added-item-price]',
      addedItemOptions: '[data-added-item-options]',
      addedItemQuantity: '[data-added-item-quantity]',
      cartSummaryItems: '[data-cart-summary-items]',
      cartSummarySubtotal: '[data-cart-summary-subtotal]',
      cartSummaryTotal: '[data-cart-summary-total]',
      recommendedProducts: '[data-recommended-products]',
      freeShippingNotification: '[data-free-shipping-notification]',
      freeShippingMessage: '[data-free-shipping-message]',
      freeShippingProgressFill: '[data-free-shipping-progress-fill]',
      freeShippingAmountLeft: '[data-free-shipping-amount-left]',
    };

    // Free shipping threshold will be parsed from data attribute in init()
    this.freeShippingThreshold = null;
  }

  connectedCallback() {
    // Wait for dependencies to be ready
    if (!window.MinimogSettings || !window.MinimogSettings.enable_cart_add_popup) {
      return;
    }

    // Check if required objects exist
    if (!window.MinimogEvents || !window.MinimogTheme) {
      // Wait a bit and try again
      setTimeout(() => {
        if (window.MinimogEvents && window.MinimogTheme) {
          this.init();
        }
      }, 100);
      return;
    }

    this.init();
  }

  init() {
    console.log('Cart add popup: initializing');
    this.domNodes = this.queryDomNodes();
    console.log('Cart add popup: domNodes', this.domNodes);
    this.recommendationType = this.dataset.recommendationType || 'product_list';
    this.productLimit = parseInt(this.dataset.productLimit) || 4;
    this.collectionHandle = this.dataset.collectionHandle || '';
    
    // Parse free shipping threshold from data attribute
    this.freeShippingThreshold = this.parseFreeShippingThreshold();
    console.log('Cart add popup: free shipping threshold', this.freeShippingThreshold);
    console.log('Cart add popup: recommendationType', this.recommendationType);
    console.log('Cart add popup: collectionHandle', this.collectionHandle);
    console.log('Cart add popup: productLimit', this.productLimit);
    console.log('Cart add popup: recommendedProducts container found?', !!this.domNodes.recommendedProducts);
    
    // Check if recommendations section exists
    const recommendationsSection = this.querySelector('.m-cart-add-popup__recommendations');
    console.log('Cart add popup: recommendations section found?', !!recommendationsSection);
    if (recommendationsSection) {
      console.log('Cart add popup: recommendations section display style', window.getComputedStyle(recommendationsSection).display);
    }

    this.setupEventListeners();
    console.log('Cart add popup: event listeners setup complete');
    // Note: Collection products will be loaded when popup opens (in open() method)
    // to ensure DOM is ready and avoid duplicate loading
  }

  queryDomNodes() {
    const nodes = {};
    Object.keys(this.selectors).forEach((key) => {
      nodes[key] = this.querySelector(this.selectors[key]);
    });
    return nodes;
  }

  parseFreeShippingThreshold() {
    // Try to get threshold from data attribute
    const thresholdAttr = this.dataset.freeShippingThreshold;
    if (thresholdAttr) {
      const parsed = parseInt(thresholdAttr);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    // Default threshold: $70 (7000 cents)
    return 7000;
  }

  setupEventListeners() {
    // Close button
    if (this.domNodes.closeButton) {
      this.domNodes.closeButton.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.domNodes.overlay) {
      this.domNodes.overlay.addEventListener('click', () => this.close());
    }

    // Listen to product added event
    document.addEventListener('product-ajax:added', this.handleProductAdded.bind(this));

    // Listen to cart update event
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        this.handleCartUpdate.bind(this)
      );
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('m-cart-add-popup--active')) {
        this.close();
      }
    });
  }

  async handleProductAdded(event) {
    console.log('Cart add popup: handleProductAdded triggered', event);
    if (!window.MinimogSettings || !window.MinimogSettings.enable_cart_add_popup) {
      console.log('Cart add popup: disabled in settings');
      return;
    }

    // Fetch full cart data from API
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      console.log('Cart add popup: fetched cart data', cart);
      
      if (cart && cart.items && cart.items.length > 0) {
        const lastItem = cart.items[cart.items.length - 1];
        console.log('Cart add popup: showing popup with item', lastItem);
        this.showPopup(lastItem, cart);
      } else {
        console.log('Cart add popup: no cart items found in fetched cart');
      }
    } catch (error) {
      console.error('Cart add popup: error fetching cart', error);
    }
  }

  async handleCartUpdate(event) {
    console.log('Cart add popup: handleCartUpdate triggered', event);
    if (!window.MinimogSettings || !window.MinimogSettings.enable_cart_add_popup) {
      console.log('Cart add popup: disabled in settings');
      return;
    }

    // Only show popup if we have product variant ID (meaning it's from add to cart)
    if (event?.productVariantId) {
      // Fetch full cart data to ensure we have items
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        console.log('Cart add popup: fetched cart in handleCartUpdate', cart);
        
        if (cart && cart.items && cart.items.length > 0) {
          const lastItem = cart.items[cart.items.length - 1];
          // Only show if this is a new item (check if variant ID matches)
          if (lastItem.variant_id && parseInt(lastItem.variant_id) === parseInt(event.productVariantId)) {
            console.log('Cart add popup: showing popup via cartUpdate', lastItem);
            this.showPopup(lastItem, cart);
          } else {
            console.log('Cart add popup: variant ID mismatch', lastItem.variant_id, event.productVariantId);
          }
        } else {
          console.log('Cart add popup: no items in cart');
        }
      } catch (error) {
        console.error('Cart add popup: error fetching cart in handleCartUpdate', error);
      }
    } else {
      console.log('Cart add popup: missing productVariantId in cartUpdate event');
    }
  }

  showPopup(item, cart) {
    console.log('Cart add popup: showPopup called', item, cart);
    if (!this.domNodes) {
      console.error('Cart add popup: domNodes not initialized');
      return;
    }
    this.populateAddedItem(item);
    this.populateCartSummary(cart);
    this.open();
    console.log('Cart add popup: popup opened');
  }

  populateAddedItem(item) {
    // Image
    if (this.domNodes.addedItemImage) {
      const imageUrl = item.image
        ? item.image.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_200x200.$1')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png';
      this.domNodes.addedItemImage.src = imageUrl;
      this.domNodes.addedItemImage.alt = item.product_title || '';
    }

    // Title
    if (this.domNodes.addedItemTitle) {
      this.domNodes.addedItemTitle.textContent = item.product_title || '';
    }

    // Price
    if (this.domNodes.addedItemPrice) {
      const price = this.formatMoney(item.final_line_price || item.line_price || item.price);
      this.domNodes.addedItemPrice.textContent = price;
    }

    // Options
    if (this.domNodes.addedItemOptions) {
      const options = [];
      if (item.variant_title && item.variant_title !== 'Default Title') {
        // Parse variant title for options (format: "Color: White / Size: M")
        const variantParts = item.variant_title.split(' / ');
        variantParts.forEach((part) => {
          if (part.includes(':')) {
            const [key, value] = part.split(':').map((s) => s.trim());
            options.push(`${key}: ${value}`);
          } else {
            options.push(part);
          }
        });
      }
      this.domNodes.addedItemOptions.textContent = options.join(', ') || '';
    }

    // Quantity
    if (this.domNodes.addedItemQuantity) {
      const quantityText = window.MinimogStrings?.quantity || 'Quantity';
      this.domNodes.addedItemQuantity.textContent = `${quantityText}: ${item.quantity || 1}`;
    }
  }

  populateCartSummary(cart) {
    // Items count
    if (this.domNodes.cartSummaryItems) {
      const itemText = cart.item_count === 1 ? 'item' : 'items';
      this.domNodes.cartSummaryItems.textContent = `${cart.item_count} ${itemText}`;
    }

    const currentTotal = cart.items_subtotal_price || cart.total_price;
    const currentTotalCents = parseInt(currentTotal);

    // Subtotal (products total)
    if (this.domNodes.cartSummarySubtotal) {
      const subtotalLabel = window.MinimogStrings?.subtotal || 'Subtotal';
      const subtotalPrice = this.formatMoney(currentTotalCents);
      this.domNodes.cartSummarySubtotal.innerHTML = `
        <span>${subtotalLabel}</span>
        <span>${subtotalPrice}</span>
      `;
    }

    // Total
    if (this.domNodes.cartSummaryTotal) {
      const totalLabel = window.MinimogStrings?.total || 'Total';
      const totalPrice = this.formatMoney(currentTotalCents);
      this.domNodes.cartSummaryTotal.innerHTML = `
        <span>${totalLabel}</span>
        <span>${totalPrice}</span>
      `;
    }

    // Update free shipping notification
    this.updateFreeShippingNotification(cart);
  }

  updateFreeShippingNotification(cart) {
    if (!this.domNodes.freeShippingNotification) {
      return;
    }

    const currentTotal = cart.items_subtotal_price || cart.total_price;
    const currentTotalCents = parseInt(currentTotal);

    // Check if free shipping threshold is reached
    const isAchieved = currentTotalCents >= this.freeShippingThreshold;
    
    if (isAchieved) {
      this.showFreeShippingNotification(currentTotalCents, true);
    } else {
      this.showFreeShippingNotification(currentTotalCents, false);
    }
  }

  showFreeShippingNotification(currentTotalCents, isAchieved) {
    if (!this.domNodes.freeShippingNotification) {
      return;
    }

    const notification = this.domNodes.freeShippingNotification;
    const amountNeeded = this.freeShippingThreshold - currentTotalCents;
    const progress = Math.min((currentTotalCents / this.freeShippingThreshold) * 100, 100);

    // Show notification
    notification.classList.add('m-cart-add-popup__free-shipping-notification--active');
    if (isAchieved) {
      notification.classList.add('m-cart-add-popup__free-shipping-notification--achieved');
    } else {
      notification.classList.remove('m-cart-add-popup__free-shipping-notification--achieved');
    }

    // Update message
    if (this.domNodes.freeShippingMessage) {
      if (isAchieved) {
        const achievedText = `Congratulations! You've unlocked <strong>FREE Shipping</strong>!`;
        this.domNodes.freeShippingMessage.innerHTML = achievedText;
      } else {
        const amountFormatted = this.formatMoney(Math.max(0, amountNeeded));
        const messageText = `Buy <strong>${amountFormatted}</strong> more to enjoy <strong>FREE Shipping</strong>`;
        this.domNodes.freeShippingMessage.innerHTML = messageText;
      }
    }

    // Update progress bar
    if (this.domNodes.freeShippingProgressFill) {
      // Force reflow to ensure transition works
      this.domNodes.freeShippingProgressFill.style.width = '0%';
      // Use requestAnimationFrame to ensure DOM update before setting new width
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.domNodes.freeShippingProgressFill.style.width = `${progress}%`;
        });
      });
    }

    // Update amount left
    if (this.domNodes.freeShippingAmountLeft) {
      if (isAchieved) {
        this.domNodes.freeShippingAmountLeft.textContent = 'You qualify for free shipping!';
      } else {
        const amountFormatted = this.formatMoney(Math.max(0, amountNeeded));
        this.domNodes.freeShippingAmountLeft.textContent = `${amountFormatted} left for free shipping`;
      }
    }
  }

  hideFreeShippingNotification() {
    if (this.domNodes.freeShippingNotification) {
      this.domNodes.freeShippingNotification.classList.remove('m-cart-add-popup__free-shipping-notification--active');
    }
  }

  formatMoney(cents) {
    // Use global formatMoney function if available (from theme-global.js)
    if (typeof window.formatMoney === 'function') {
      const moneyFormat = window.MinimogSettings?.money_format || '{{amount}}';
      return window.formatMoney(cents, moneyFormat);
    }
    
    // Fallback if formatMoney is not available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.MinimogSettings?.money_format || '{{amount}}');
    }
    
    // Last resort fallback
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.MinimogSettings?.currency_code || 'USD',
    }).format(cents / 100);
  }

  async loadRecommendedProducts() {
    if (this.recommendationType !== 'collection' || !this.collectionHandle) {
      console.log('Cart popup: skipping load - type:', this.recommendationType, 'handle:', this.collectionHandle);
      return;
    }

    if (!this.domNodes.recommendedProducts) {
      console.error('Cart popup: recommendations container not found');
      return;
    }

    console.log('Cart popup: loading recommended products from collection:', this.collectionHandle);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(
        `/collections/${this.collectionHandle}/products.json?limit=${this.productLimit}`,
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Cart popup: received products data', data);
      
      if (data.products && data.products.length > 0) {
        this.renderRecommendedProducts(data.products);
      } else {
        console.warn('Cart popup: no products in collection response');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading recommended products:', error);
      } else {
        console.warn('Cart popup: request timeout loading products');
      }
    }
  }

  async renderRecommendedProducts(products) {
    // Re-query container in case DOM changed
    const recommendationsSection = this.querySelector('.m-cart-add-popup__recommendations');
    const recommendedProductsGrid = this.querySelector('[data-recommended-products]');
    
    if (!recommendedProductsGrid) {
      console.error('Cart popup: recommendations grid container not found');
      if (recommendationsSection) {
        console.error('Cart popup: recommendations section exists but grid not found');
        console.log('Cart popup: recommendations section HTML:', recommendationsSection.innerHTML);
      }
      return;
    }

    // Update domNodes reference
    this.domNodes.recommendedProducts = recommendedProductsGrid;

    console.log('Cart popup: rendering recommended products', products);

    // Clear existing content
    recommendedProductsGrid.innerHTML = '';

    if (!products || products.length === 0) {
      console.warn('Cart popup: no products to render');
      recommendedProductsGrid.innerHTML = '<div style="text-align: center; padding: 20px; color: #666;">No recommended products available</div>';
      return;
    }

    // Render each product
    let renderedCount = 0;
    for (const product of products) {
      try {
        const productCard = await this.createProductCard(product);
        if (productCard) {
          recommendedProductsGrid.appendChild(productCard);
          // Add click handler to close popup when clicking on product
          this.attachProductCardClickHandler(productCard);
          renderedCount++;
          console.log('Cart popup: rendered product', product.title);
        } else {
          console.warn('Cart popup: failed to create card for product', product.handle);
        }
      } catch (error) {
        console.error('Cart popup: error rendering product', product.handle, error);
      }
    }
    
    console.log('Cart popup: rendered', renderedCount, 'out of', products.length, 'products');
    console.log('Cart popup: grid now has', recommendedProductsGrid.children.length, 'children');
    
    // Ensure recommendations section is visible
    if (recommendationsSection) {
      recommendationsSection.style.display = 'block';
      recommendationsSection.style.visibility = 'visible';
      recommendationsSection.style.opacity = '1';
      console.log('Cart popup: recommendations section is now visible');
      console.log('Cart popup: recommendations section computed style', {
        display: window.getComputedStyle(recommendationsSection).display,
        visibility: window.getComputedStyle(recommendationsSection).visibility,
        opacity: window.getComputedStyle(recommendationsSection).opacity,
        height: window.getComputedStyle(recommendationsSection).height
      });
    }
    
    // Also attach handlers using the main method for consistency
    this.attachRecommendedProductsClickHandlers();
  }

  attachProductCardClickHandler(productCard) {
    // Find all links in the product card (main product links)
    const links = productCard.querySelectorAll('a[href*="/products/"]');
    
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        // Close popup immediately when clicking product link
        this.close();
        // Don't prevent default - let the link navigate normally
        // Popup will close and then page will navigate to product detail
      }, { once: true });
    });
    
    // Also handle click on the entire recommendation item for better UX
    const recommendationItem = productCard.closest('.m-cart-add-popup__recommendation-item');
    if (recommendationItem && !recommendationItem.dataset.clickHandlerAttached) {
      recommendationItem.addEventListener('click', (e) => {
        // Check if click is on a product link
        const productLink = e.target.closest('a[href*="/products/"]');
        if (productLink && !e.target.closest('button') && !e.target.closest('.m-product-card__actions')) {
          // Close popup when clicking on product card (but not on action buttons)
          this.close();
        }
      }, { once: true });
      recommendationItem.dataset.clickHandlerAttached = 'true';
    }
  }

  async createProductCard(product) {
    // Fetch product HTML from theme
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(`/products/${product.handle}?view=card`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productCard = doc.querySelector('.m-product-card');

      if (productCard) {
        const wrapper = document.createElement('div');
        wrapper.className = 'm-cart-add-popup__recommendation-item';
        wrapper.setAttribute('data-product-handle', product.handle);
        
        // Clone node deeply to include all nested elements and images
        const clonedCard = productCard.cloneNode(true);
        
        // Force load images by converting lazy loading to eager or removing lazy attribute
        const images = clonedCard.querySelectorAll('img[loading="lazy"]');
        images.forEach(img => {
          img.loading = 'eager';
          // If image has data-src, move it to src
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
        });
        
        wrapper.appendChild(clonedCard);
        return wrapper;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching product card HTML:', error);
      }
    }

    // Fallback: Create product card from JSON data
    const wrapper = document.createElement('div');
    wrapper.className = 'm-cart-add-popup__recommendation-item';
    wrapper.setAttribute('data-product-handle', product.handle);
    
    // Get image with proper size
    let imageHtml = '';
    if (product.images && product.images.length > 0) {
      // Handle both string and object formats
      let imageUrl = '';
      const firstImage = product.images[0];
      
      if (typeof firstImage === 'string') {
        // If it's a string URL
        imageUrl = firstImage;
      } else if (firstImage && typeof firstImage === 'object') {
        // If it's an object with src property (Shopify API JSON format)
        if (firstImage.src) {
          imageUrl = firstImage.src;
        } else {
          console.warn('Cart popup: product image object has no src property', firstImage);
          imageUrl = '';
        }
      } else {
        // Fallback
        console.warn('Cart popup: unexpected image format', typeof firstImage, firstImage);
        imageUrl = '';
      }
      
      // Resize image to appropriate size for popup (Shopify image transformation)
      if (imageUrl && typeof imageUrl === 'string' && imageUrl.length > 0) {
        // Replace or append size parameter using Shopify image transformation
        // Format: URL -> URL_300x300.jpg
        if (imageUrl.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i)) {
          // Check if already has size transformation
          if (!imageUrl.match(/_\d+x\d+\./)) {
            imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i, '_300x300.$1$2');
          }
        }
        imageHtml = `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="eager" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover;">`;
      } else {
        imageHtml = '<div style="background: #f5f5f5; width: 100%; height: 100%; position: absolute; top: 0; left: 0;"></div>';
      }
    } else {
      imageHtml = '<div style="background: #f5f5f5; width: 100%; height: 100%; position: absolute; top: 0; left: 0;"></div>';
    }
    
    // Get price
    const price = product.variants && product.variants.length > 0 && product.variants[0]
      ? this.formatMoney(product.variants[0].price)
      : '';

    wrapper.innerHTML = `
      <div class="m-product-card m-product-card--style-1">
        <a href="/products/${product.handle}" class="m-product-card__link">
          <div class="m-product-card__image" style="position: relative; width: 100%; padding-top: 100%; overflow: hidden; border-radius: 8px;">
            ${imageHtml}
          </div>
        </a>
        <div class="m-product-card__info" style="padding: 12px 0;">
          <h3 class="m-product-card__title" style="font-size: 14px; margin: 0 0 8px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
            <a href="/products/${product.handle}" style="text-decoration: none; color: inherit;">
              ${this.escapeHtml(product.title)}
            </a>
          </h3>
          <div class="m-product-card__price" style="font-size: 15px; font-weight: 600;">
            ${price}
          </div>
        </div>
      </div>
    `;

    return wrapper;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  open() {
    console.log('Cart add popup: open() called');
    this.classList.add('m-cart-add-popup--active');
    document.documentElement.classList.add('prevent-scroll');
    console.log('Cart add popup: classes added, popup should be visible');
    
    // On mobile, scroll to recommendations after a short delay to ensure they're visible
    if (window.innerWidth <= 767) {
      setTimeout(() => {
        const recommendationsSection = this.querySelector('.m-cart-add-popup__recommendations');
        if (recommendationsSection) {
          recommendationsSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 300);
    }
    
    // Ensure DOM nodes are available
    if (!this.domNodes) {
      this.domNodes = this.queryDomNodes();
    }
    
    // Ensure recommendations section is visible
    const recommendationsSection = this.querySelector('.m-cart-add-popup__recommendations');
    const recommendedProductsGrid = this.querySelector('[data-recommended-products]');
    
    if (recommendationsSection) {
      recommendationsSection.style.display = 'block';
      recommendationsSection.style.visibility = 'visible';
      recommendationsSection.style.opacity = '1';
      console.log('Cart popup: ensuring recommendations section is visible');
      console.log('Cart popup: recommendations section exists');
    } else {
      console.error('Cart popup: recommendations section NOT FOUND in DOM!');
      console.error('Cart popup: all recommendations elements:', this.querySelectorAll('[class*="recommendation"]'));
    }
    
    // Update domNodes reference
    if (recommendedProductsGrid) {
      this.domNodes.recommendedProducts = recommendedProductsGrid;
      console.log('Cart popup: recommendations grid container found');
      console.log('Cart popup: grid current children count', recommendedProductsGrid.children.length);
      console.log('Cart popup: grid HTML preview', recommendedProductsGrid.innerHTML.substring(0, 200));
    } else {
      console.error('Cart popup: recommendations grid NOT FOUND!');
    }
    
    // Load recommended products if type is collection (only when popup opens)
    if (this.recommendationType === 'collection' && this.collectionHandle) {
      // Check if products already loaded to avoid reloading unnecessarily
      const existingProducts = this.domNodes.recommendedProducts?.querySelectorAll('.m-cart-add-popup__recommendation-item');
      console.log('Cart popup: existing products count', existingProducts?.length || 0);
      if (!existingProducts || existingProducts.length === 0) {
        console.log('Cart popup: loading collection products');
        this.loadRecommendedProducts();
      } else {
        console.log('Cart popup: products already loaded, skipping');
      }
    } else if (this.recommendationType === 'product_list') {
      console.log('Cart popup: using product_list type, checking for products...');
      const existingProducts = this.domNodes.recommendedProducts?.querySelectorAll('.m-cart-add-popup__recommendation-item');
      console.log('Cart popup: product_list products count', existingProducts?.length || 0);
      if (!existingProducts || existingProducts.length === 0) {
        console.warn('Cart popup: product_list type but no products found! Check Shopify settings.');
      }
    } else {
      console.warn('Cart popup: no recommendation type set or invalid type:', this.recommendationType);
    }
    
    // Attach click handlers to recommended products (for product_list type)
    this.attachRecommendedProductsClickHandlers();
  }

  attachRecommendedProductsClickHandlers() {
    // Handle products rendered from server (product_list type)
    const recommendationItems = this.querySelectorAll('.m-cart-add-popup__recommendation-item');
    recommendationItems.forEach(item => {
      // Only attach if not already attached (check for data attribute)
      if (!item.dataset.clickHandlerAttached) {
        // Find product links (links to product pages)
        const productLinks = item.querySelectorAll('a[href*="/products/"]');
        productLinks.forEach(link => {
          link.addEventListener('click', (e) => {
            // Don't close if clicking on action buttons (quick add, wishlist, etc.)
            if (e.target.closest('button') || e.target.closest('.m-product-card__actions')) {
              return;
            }
            // Close popup immediately before navigation
            this.close();
            // Don't prevent default - let the link navigate normally
          }, { once: true });
        });
        
        // Also handle click on the entire card area for better UX
        item.addEventListener('click', (e) => {
          const productLink = e.target.closest('a[href*="/products/"]');
          if (productLink && !e.target.closest('button') && !e.target.closest('.m-product-card__actions')) {
            // Close popup when clicking on product card (but not on action buttons)
            this.close();
          }
        }, { once: true });
        
        item.dataset.clickHandlerAttached = 'true';
      }
    });
  }

  close() {
    this.classList.remove('m-cart-add-popup--active');
    document.documentElement.classList.remove('prevent-scroll');
  }
}

customElements.define('m-cart-add-popup', MCartAddPopup);

